import { ChevronDownIcon, ChevronUpIcon } from "$components/icons";
import {
  IconButton,
  TableRow as MUITableRow,
  makeStyles,
} from "@material-ui/core";
import classNames from "classnames";
import { useCallback, useState } from "react";
import TableCell from "./TableCell";
import TableRow from "./TableRow";
import type {
  EditedRowProps,
  GroupedTableRowDefinition,
  TableProps,
} from "./types";

const useStyles = makeStyles((theme) => ({
  groupHeaderRow: {
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
    // Background lives on the row so sticky cells (which inherit) stay grey
    // along with the rest of the group header instead of falling back to
    // the body row's paper colour.
    backgroundColor: theme.palette.grey[100],
  },
  boldRow: {
    fontWeight: "bold",
  },
}));

interface GroupedTableRowProps {
  row: GroupedTableRowDefinition;
  index: number;

  onExpand?(): any;
}

const GroupedTableRow = ({
  row,
  index,
  onExpand,
  ...props
}: GroupedTableRowProps & TableProps & EditedRowProps) => {
  const { columns, expandingGroups, groupsInitiallyOpen, classes } = props;
  const rowClasses = useStyles();
  // Without expandingGroups there is no expander affordance, so groups must
  // stay open regardless of groupsInitiallyOpen.
  const [open, setOpen] = useState(
    expandingGroups ? (groupsInitiallyOpen ?? false) : true,
  );

  const onExpandClick = useCallback(() => {
    if (onExpand) onExpand();
    setOpen((isOpen) => !isOpen);
  }, [onExpand]);

  const headerRow = (
    <MUITableRow
      hover
      data-testid={`header-row-${index}`}
      className={classNames(
        rowClasses.groupHeaderRow,
        classes?.expandingHeaderClass,
        classes?.rowClass?.(row.header, index),
      )}
    >
      {expandingGroups && (
        <TableCell
          row={row.header}
          column={columns[0]}
          index={index}
          cellIndex={0}
          className={classNames(classes?.expandingHeaderClass)}
          {...props}
        >
          <IconButton
            aria-label={open ? "collapse group" : "expand group"}
            onClick={onExpandClick}
            style={{ padding: 0 }}
          >
            {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </IconButton>
        </TableCell>
      )}
      {columns.slice(expandingGroups ? 1 : 0).map((column, idx) => (
        <TableCell
          row={row.header}
          column={column}
          index={index}
          cellIndex={idx}
          isGroupHeaderCell
          key={`${index}-${column.field}`}
          className={classNames(
            rowClasses.boldRow,
            classes?.expandingHeaderClass,
          )}
          {...props}
        />
      ))}
    </MUITableRow>
  );

  return (
    <>
      {headerRow}
      {open &&
        row.children.map((child, idx) => (
          <TableRow
            hover={false}
            index={idx}
            row={child}
            key={`row-${child.id}`}
            className={classNames(classes?.expandedRowClass)}
            {...props}
          />
        ))}
    </>
  );
};

GroupedTableRow.defaultProps = {
  onExpand: null,
};

export default GroupedTableRow;
